import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Lock, Check } from 'lucide-react';

export default function UpdatePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState({
    password: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();
  const location = useLocation();

  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    const checkRecoverySession = async () => {
      if (!supabase) {
        setMessage({
          type: 'error',
          text: 'Unable to initialize authentication. Please try again later.'
        });
        return;
      }

      const params = new URLSearchParams(location.search);
      // A "+" in the token is turned into a space by some mail clients.
      const tokenHash = params.get('token_hash')?.replace(/ /g, '+') ?? null;
      const type = params.get('type');
      const code = params.get('code');

      if (tokenHash && type === 'recovery') {
        // Direct token verification (from our custom SendGrid email)
        console.log('Password reset token_hash detected, verifying...');
        try {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });

          if (verifyError) {
            throw verifyError;
          }
          console.log('✅ Recovery token verified, session established');
          setSessionReady(true);
        } catch (err) {
          console.error('Error verifying recovery token:', err);
          setMessage({
            type: 'error',
            text: 'Invalid or expired password reset link. Please request a new one.'
          });
        }
      } else if (code) {
        // PKCE code exchange (from Supabase verify redirect)
        console.log('Password reset code detected, exchanging...');
        try {
          const { data, error } = await supabase.auth.getSession();
          if (error) throw error;

          if (!data?.session) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
            if (exchangeError) throw exchangeError;
          }
          console.log('✅ Code exchanged, session established');
          setSessionReady(true);
        } catch (err) {
          console.error('Error exchanging code:', err);
          setMessage({
            type: 'error',
            text: 'Invalid or expired password reset link. Please request a new one.'
          });
        }
      } else {
        // No token or code — check for existing session
        const { data, error } = await supabase.auth.getSession();
        if (error || !data?.session) {
          setMessage({
            type: 'error',
            text: 'Invalid or expired password reset link. Please request a new one.'
          });
        } else {
          setSessionReady(true);
        }
      }
    };
    
    checkRecoverySession();
  }, [navigate, location.search]);

  const validateForm = () => {
    const errors = {
      password: '',
      confirmPassword: ''
    };

    // Password must be at least 8 characters
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    // Confirm passwords match
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return !errors.password && !errors.confirmPassword;
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setMessage(null);
    setValidationErrors({
      password: '',
      confirmPassword: ''
    });

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }
      
      const { error } = await supabase.auth.updateUser({
        password: password.trim()
      });

      if (error) {
        throw error;
      }

      setMessage({
        type: 'success',
        text: 'Your password has been updated successfully! You will be redirected to the login page in a few seconds.'
      });
      
      // After successful password update, redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 5000);
    } catch (err) {
      console.error('Password update error:', err);
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to update password. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a button to go back to request a new link
  const handleRequestNewLink = () => {
    navigate('/reset-password', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="h-12 w-12 bg-gray-900 rounded-lg flex items-center justify-center mx-auto">
          <Lock className="h-6 w-6 text-white" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Update your password
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleUpdatePassword}>
            {message && (
              <div className={`${
                message.type === 'success' ? 'bg-green-50 border-green-200 text-green-600' : 'bg-red-50 border-red-200 text-red-600'
              } px-4 py-3 rounded-md text-sm border`}>
                {message.text}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`block w-full appearance-none rounded-md border ${
                    validationErrors.password ? 'border-red-300' : 'border-gray-300'
                  } pl-10 pr-3 py-2 placeholder-gray-400 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-gray-500 sm:text-sm`}
                  placeholder="••••••••"
                />
              </div>
              {validationErrors.password && (
                <p className="mt-2 text-sm text-red-600">{validationErrors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <div className="mt-1 relative">
                <Check className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`block w-full appearance-none rounded-md border ${
                    validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } pl-10 pr-3 py-2 placeholder-gray-400 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-gray-500 sm:text-sm`}
                  placeholder="••••••••"
                />
              </div>
              {validationErrors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600">{validationErrors.confirmPassword}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || message?.type === 'success' || (!sessionReady && !message)}
                className="flex w-full justify-center rounded-md border border-transparent bg-gray-900 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!sessionReady && !message ? 'Verifying link...' : loading ? 'Updating password...' : message?.type === 'success' ? 'Password updated!' : 'Update password'}
              </button>
            </div>
            
            {message?.type === 'error' && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleRequestNewLink}
                  className="text-sm font-medium text-gray-900 hover:text-gray-800"
                >
                  Request a new password reset link
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
} 